import type { AppScreenColumn, ChampAChoix, VerdictNatif } from '@taki/data-model';
import { CHAMPS_D_IDENTITE, PREFIXE_CHAMP_SOCLE, choixIncompatibles, customFieldsField, estChampDeTrace, validerChampNatif, viderLesDependants } from '@taki/data-model';
import type { AccesJeu, Cell, ValeurEcrite } from '@taki/ui';
import { refuser } from '@taki/ui';
import type { FicheGenerique } from './app-declaree.js';

/**
 * L'ACCES AU JEU D'UNE PORTE : lire une clef du sac, lire un champ natif, et
 * ecrire les deux.
 *
 * C'ETAIT LE DERNIER TYPESCRIPT D'UNE APP, et c'est pour ca que ce module
 * existe. Chaque ecran ecrit a la main portait sa table d'ecrivains, une entree
 * par jeu, avec les memes quinze lignes : lire le sac, lire la racine, ecrire le
 * sac, ecrire un natif, refuser ce que la famille ne porte pas. Rien la-dedans
 * n'etait propre a une app : ce qui l'etait, c'etaient les REFUS (le nom d'une
 * societe ne se vide pas, un email a une forme), et les deux se declarent
 * desormais, l'un sur la colonne (`requis`), l'autre dans le schema de la
 * famille (`validerChampNatif`).
 *
 * TOUT REFUS PASSE PAR `refuser()`, JAMAIS PAR UN `throw new Error`. C'est ce
 * qui met LA PHRASE dans la pastille de la rangee 3 a la place de « Echec de
 * l'enregistrement », et retire un « Reessayer » qui rejouerait le meme refus
 * (`packages/ui/src/views/refus.ts`). Un `Error` nu redeviendrait une panne
 * muette, en console.
 *
 * FONCTION PURE DE REACT ET DE FIREBASE : elle ne connait qu'un DEPOT (deux
 * verbes) et un catalogue de colonnes. C'est ce qui la rend prouvable avec un
 * faux depot de trois lignes.
 */

/** Ce que l'acces demande au SDK : le nom de la famille, et de quoi patcher. */
export interface DepotDeJeu {
  /** Le nom de la famille, au pluriel (`contacts`), tel que la porte le declare. */
  readonly famille: string;
  update(id: string, patch: Record<string, unknown>): Promise<void>;
}

/** La validation d'un champ natif. Le defaut est celle du socle ; un test la remplace. */
export type ValidateurNatif = (
  famille: string,
  key: string,
  valeur: unknown,
) => VerdictNatif;

/**
 * UNE VALEUR D'EDITEUR, PRETE POUR LE SAC `customFields`.
 *
 * UNE SAISIE VIDE VAUT `null`, et ce n'est pas un detail : le SDK SUPPRIME alors
 * la clef du document (`deleteField`), au lieu de laisser une chaine vide qui se
 * relirait comme « renseigne, mais vide ».
 *
 * UNE LISTE (un choix multiple) PART TELLE QUELLE, VIDE COMPRISE : `[]` dit
 * « aucun jour coche », ce qui est une reponse, et la joindre en texte ferait
 * relire « lun,ven » comme une seule clef. Une copie, jamais la liste de
 * l'appelant : le SDK la garde dans son patch.
 */
function versValeurDeSac(valeur: ValeurEcrite): string | number | boolean | string[] | null {
  if (isListe(valeur)) return [...valeur];
  if (valeur === null || valeur === '') return null;
  if (typeof valeur === 'number' || typeof valeur === 'boolean') return valeur;
  if (valeur instanceof Date) return valeur.toISOString();
  return valeur;
}

/**
 * LES DEUX CHAMPS STANDARD DE L'IDENTITE D'UNE FICHE, ecrits comme des natifs
 * (`docs/socle/couleur-photo-fiche.md`) : `couleur` et `photo` sont sur toute
 * fiche (`baseEntityFields`), mais ne sont pas des COLONNES d'une famille, donc
 * `validerChampNatif` les refuserait (« n'est pas un champ de la famille »).
 * Le schema du socle fait foi, une seule fois : `customFieldsField`.
 *
 * UNE CASE VIDEE (`null`, chaine vide) EST VALIDE ET VAUT `null` : retirer la
 * couleur ou la photo est un geste, pas une faute. Les deux sont facultatives.
 */

export function validerIdentite(nom: (typeof CHAMPS_D_IDENTITE)[number], valeur: unknown): VerdictNatif {
  if (valeur === null || valeur === undefined || (typeof valeur === 'string' && valeur.trim() === '')) {
    return { ok: true, valeur: null };
  }
  const verdict = customFieldsField[nom].safeParse(valeur);
  if (verdict.success) return { ok: true, valeur: verdict.data };
  return {
    ok: false,
    raison: nom === 'couleur'
      ? "La couleur n'est ni une couleur du design ni un hex (#rgb, #rrggbb)."
      : "L'identifiant de la photo n'est pas valide.",
  };
}

const estChampDIdentite = (nom: string): nom is (typeof CHAMPS_D_IDENTITE)[number] =>
  (CHAMPS_D_IDENTITE as readonly string[]).includes(nom);

/** Une liste de clefs, et pas une cellule. `readonly` ne passe pas `Array.isArray` en TypeScript. */
const isListe = (valeur: ValeurEcrite): valeur is readonly string[] => Array.isArray(valeur);

/** Une valeur du document, ramenee a ce qu'une vue sait afficher. */
function cellule(brut: unknown): Cell {
  if (brut === undefined || brut === null || brut === '') return null;
  if (brut instanceof Date) return brut;
  /* UNE LISTE DEVIENT UNE LIGNE PAR ELEMENT (le journal d'un plan, ses sauts,
     un choix multiple) : la fiche rend les retours a la ligne, un tableau les
     lit comme des espaces. `String(liste)` collait tout avec des virgules, et
     un journal de quarante lignes devenait un seul paragraphe illisible. Une
     liste vide est une case vide. */
  if (Array.isArray(brut)) {
    const lignes = brut.map((element) => (typeof element === 'string' ? element : String(element)));
    return lignes.length === 0 ? null : lignes.join('\n');
  }
  /* UN BOOLEEN RESTE UN BOOLEEN : `Cell` l'accepte et les vues le rendent
     « Oui » / « Non » ; en chaine, il s'afficherait « true ». */
  return typeof brut === 'number' || typeof brut === 'string' || typeof brut === 'boolean'
    ? brut
    : String(brut);
}

/** L'identifiant d'une fiche, lu sans rien supposer de son jeu. */
const idDeFiche = (fiche: FicheGenerique): string => String(fiche['id'] ?? '');

/**
 * LE MOT DE LA COLONNE QUI REFUSE DE SE VIDER, ou `null`.
 *
 * `requis` VIT SUR LA COLONNE ET PAS DANS LE SCHEMA DE LA FAMILLE, et les deux
 * disent deux choses differentes : le schema dit ce qui est VALIDE pour toutes
 * les apps (`name` peut manquer sur une personne), la colonne dit ce que CET
 * ecran exige (la raison sociale d'une societe est son titre). Le schema ne peut
 * donc pas porter ce refus, et c'est le catalogue de l'ecran qui le porte.
 */
function motDeColonneRequise(
  colonnes: readonly AppScreenColumn[],
  nom: string,
): string | null {
  const colonne = colonnes.find(
    (c) => c.requis === true && c.champ === `${PREFIXE_CHAMP_SOCLE}${nom}`,
  );
  return colonne === undefined ? null : (colonne.label ?? nom);
}

/**
 * L'ACCES AU JEU D'UNE PORTE.
 *
 * SANS DEPOT, LA LECTURE SEULE : une porte dont la declaration ne nomme aucun
 * jeu ne peut rien ecrire, et le dire est un fait. Une case qui invite au clic
 * puis refuse serait un mensonge, et l'absence de `ecrire` est exactement ce que
 * le contrat des vues lit pour ne pas rendre l'affordance.
 */
export function accesDuJeu(
  depot: DepotDeJeu | null,
  colonnes: readonly AppScreenColumn[],
  valider: ValidateurNatif = validerChampNatif,
  /** Les champs declares du jeu : ce qui porte les choix dependants (`dependDe`). */
  champs: readonly ChampAChoix[] = [],
): AccesJeu<FicheGenerique> {
  const lecture: Pick<AccesJeu<FicheGenerique>, 'lire' | 'natif' | 'natifEnLectureSeule'> = {
    lire: (record, key) => {
      const sac = record['customFields'];
      return cellule(
        sac !== null && typeof sac === 'object'
          ? (sac as Record<string, unknown>)[key]
          : undefined,
      );
    },
    natif: (record, nom) => cellule(record[nom]),
    /*
     * UNE TRACE SE LIT, ELLE NE S'ECRIT PAS : `createdBy`, `updatedBy`,
     * `createdAt`, `updatedAt` sont poses par le SDK (les rules l'exigent, et
     * tiennent `createdBy` / `createdAt` pour immuables). Le dire ICI retire
     * l'editeur de la case (G16) au lieu d'offrir un clic qui serait refuse.
     */
    natifEnLectureSeule: (nom) => estChampDeTrace(nom),
  };
  if (depot === null) return { ...lecture };

  return {
    ...lecture,
    /*
     * LE SAC S'ECRIT CLEF PAR CLEF, et le SDK le fusionne de meme : un patch ne
     * porte QUE la clef touchee, donc les clefs posees par les autres apps du
     * pack (le `demoKey` d'un jeu de demonstration compris) survivent intactes.
     */
    /*
     * LES CHOIX DEPENDANTS (`dependDe`) SE TIENNENT ICI, dans le meme patch :
     * un Job qui n'appartient pas au Pole de la fiche est refuse avec sa
     * phrase, et changer de Pole vide le Job devenu incompatible. Le patch
     * porte alors deux clefs, et seule celle qu'on a touchee a son optimisme :
     * l'autre se relit au retour du serveur.
     */
    ecrire: async (record, key, valeur) => {
      const sac = record['customFields'];
      const lire = (clef: string): unknown =>
        sac !== null && typeof sac === 'object' ? (sac as Record<string, unknown>)[clef] : undefined;
      const ecrite = versValeurDeSac(valeur);
      const refus = choixIncompatibles(champs, (clef) => (clef === key ? ecrite : lire(clef)))
        .find((faute) => faute.champ === key);
      if (refus !== undefined) refuser(refus.phrase);
      await depot.update(idDeFiche(record), {
        customFields: { [key]: ecrite, ...viderLesDependants(champs, key, ecrite, lire) },
      });
    },
    /*
     * UN CHAMP NATIF NE VIT PAS DANS LE SAC, donc il ne s'ecrit pas comme lui :
     * il est une colonne de la famille, et c'est le schema qui dit ce qu'elle
     * accepte. DEUX REFUS, dans cet ordre, et ils ne disent pas la meme chose :
     * le schema refuse une valeur FAUSSE (une adresse sans arobase, un nombre
     * la ou il faut un texte), la colonne refuse une case VIDEE sur un champ que
     * cet ecran exige. Les intervertir ferait dire « ne peut pas etre efface »
     * a une saisie qui n'etait pas vide, mais fausse.
     *
     * `type` ET `orgId` N'ARRIVENT JAMAIS ICI : les rules les tiennent pour
     * IMMUABLES, et `champsNatifs` ne les expose pas.
     */
    ecrireNatif: async (record, nom, valeur) => {
      /* LA COULEUR ET LA PHOTO s'ecrivent a la racine de la fiche, `null` compris
         (le SDK les lit comme « retire »), sans passer par les colonnes d'une famille. */
      if (estChampDIdentite(nom)) {
        const verdictIdentite = validerIdentite(nom, valeur);
        if (!verdictIdentite.ok) refuser(verdictIdentite.raison);
        await depot.update(idDeFiche(record), { [nom]: verdictIdentite.valeur });
        return;
      }
      /* LA TRACE EST REFUSEE AVANT TOUT VALIDATEUR, meme injecte : c'est un fait
         du socle, pas du schema d'une famille. La phrase est celle du socle. */
      if (estChampDeTrace(nom)) {
        const verdictDuSocle = validerChampNatif(depot.famille, nom, valeur);
        if (!verdictDuSocle.ok) refuser(verdictDuSocle.raison);
      }
      const verdict = valider(depot.famille, nom, valeur);
      if (!verdict.ok) refuser(verdict.raison);
      const mot = motDeColonneRequise(colonnes, nom);
      /* UNE LISTE VIDEE EST UNE CASE VIDEE : tout decocher sur un choix
         multiple que l'ecran exige se refuse comme un texte efface. */
      const videe =
        verdict.valeur === null || (Array.isArray(verdict.valeur) && verdict.valeur.length === 0);
      if (mot !== null && videe) {
        refuser(`« ${mot} » ne peut pas etre efface, seulement corrige.`);
      }
      await depot.update(idDeFiche(record), { [nom]: verdict.valeur });
    },
  };
}
